/**
 * Per-brand announcement bar editor. Saves through the existing
 * updateBrandData endpoint — the backend passthrough writes the three
 * announcement_* fields straight onto the brand_profile document.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

export default function AnnouncementBar({ brandData, fetchData }) {
  const brandId = brandData?._id;
  const accent = brandData?.brand_color || '#422AFB';
  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (brandData) {
      setEnabled(!!brandData.announcement_enabled);
      setText(brandData.announcement_text || '');
      setLink(brandData.announcement_link || '');
    }
  }, [brandData]);

  const save = (overrides = {}) => {
    if (!brandId) return;
    setSaving(true);
    const payload = {
      brand_id: brandId,
      announcement_enabled: overrides.enabled ?? enabled,
      announcement_text: (overrides.text ?? text).slice(0, 240),
      announcement_link: overrides.link ?? link,
    };
    const fd = new FormData();
    fd.append('data', JSON.stringify(payload));
    Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
    API.updateBrandData(fd)
      .then(() => { toast.success('Saved'); fetchData && fetchData(); })
      .catch((err) => toast.error(err?.message || 'Failed'))
      .finally(() => setSaving(false));
  };

  if (!brandId) return null;

  const previewActive = enabled && text.trim().length > 0;

  return (
    <Card sx={{ mb: '20px', borderRadius: '20px' }}>
      <CardContent>
        <Box mb='14px' ml='10px'>
          <Typography variant='h6' fontWeight='bold'>Announcement bar</Typography>
          <Typography variant='body2' color='text.secondary'>
            A short line that sits above your storefront nav on every page.
            Good for sales, shipping cutoffs, or brand-new launches.
          </Typography>
        </Box>
        <Box mx='10px'>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            py='10px'
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Box>
              <Typography fontWeight='600'>Show announcement</Typography>
              <Typography variant='body2' color='text.secondary'>
                Toggle the bar on or off without losing the text.
              </Typography>
            </Box>
            <Switch
              checked={enabled}
              onChange={(e) => { setEnabled(e.target.checked); save({ enabled: e.target.checked }); }}
              disabled={saving}
              size='medium'
            />
          </Stack>

          <Stack spacing='10px' py='14px'>
            <Box>
              <Typography variant='body2' fontWeight='600' mb='4px'>Announcement text</Typography>
              <TextField
                fullWidth
                size='small'
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder='Free shipping on orders over $50 — today only'
                inputProps={{ maxLength: 240 }}
              />
              <Typography variant='caption' color='text.secondary' mt='4px' display='block'>
                {text.length}/240 characters
              </Typography>
            </Box>
            <Box>
              <Typography variant='body2' fontWeight='600' mb='4px'>Link (optional)</Typography>
              <TextField
                fullWidth
                size='small'
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder='/shop or https://…'
              />
            </Box>
          </Stack>

          {previewActive && (
            <Box mb='10px'>
              <Typography variant='caption' color='text.secondary' fontWeight='700' textTransform='uppercase' mb='4px' display='block'>
                Preview
              </Typography>
              <Box
                sx={{
                  bgcolor: accent,
                  color: '#fff',
                  textAlign: 'center',
                  py: '6px',
                  px: '12px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                {link ? <u>{text}</u> : text}
              </Box>
            </Box>
          )}

          <Stack direction='row' justifyContent='flex-end'>
            <Button
              variant='contained'
              size='small'
              onClick={() => save()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
